import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { client } from '..';
import { GET_EMAILS, ADD_EMAIL, EDIT_EMAIL, DELETE_EMAIL, UPDATE_EMP_EMAIL_SETTINGS } from '../Queries/Emails';
import { setLoader } from './loaderSlice';

// Fetch all emails
export const fetchEmails = createAsyncThunk(
    'emails/fetchEmails',
    async ({ silent = false } = {}, { dispatch, rejectWithValue }) => {
        try {
            if (!silent) dispatch(setLoader(true));
            const { data } = await client.query({ query: GET_EMAILS, fetchPolicy: 'network-only' });
            return data.GetEmails;
        } catch (error) {
            message.error('Failed to fetch emails.');
            return rejectWithValue(error.message);
        } finally {
            dispatch(setLoader(false));
        }
    }
);

// Add a new email
export const addEmail = createAsyncThunk(
    'emails/addEmail',
    async ({ email, password }, { dispatch, rejectWithValue }) => {

        try {
            dispatch(setLoader(true));
            const { data } = await client.mutate({
                mutation: ADD_EMAIL,
                variables: { email, password },
            });

            if (data?.AddEmail?.status === 200) {
                message.success(data.AddEmail.message);
                // Assume backend returns new email object in data.AddEmail.email
                return data.AddEmail.emails[0];
            } else {
                message.error(data.AddEmail.message || 'Failed to add email');
                return rejectWithValue(data.AddEmail.message);
            }
        } catch (error) {
            return rejectWithValue(error.message);
        } finally {
            dispatch(setLoader(false));
        }
    }
);

// Edit an existing email
export const editEmail = createAsyncThunk(
    'emails/editEmail',
    async ({ _id, email, password }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoader(true));
            const variables = { _id, email };
            if (password) variables.password = password;
            const { data } = await client.mutate({
                mutation: EDIT_EMAIL,
                variables,
            });

            if (data?.EditEmail?.status === 200) {
                message.success(data.EditEmail.message);
                // Assume backend returns updated email object in data.EditEmail.email
                return data.EditEmail.emails[0];

            } else {
                message.error(data.EditEmail.message || 'Failed to update email');
                return rejectWithValue(data.EditEmail.message);
            }
        } catch (error) {
            message.error('An error occurred.');
            return rejectWithValue(error.message);
        } finally {
            dispatch(setLoader(false));
        }
    }
);

// Delete an email
export const deleteEmail = createAsyncThunk(
    'emails/deleteEmail',
    async ({ email }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoader(true));
            const { data } = await client.mutate({
                mutation: DELETE_EMAIL,
                variables: { email },
            });

            if (data?.DeleteEmail?.status === 200) {
                message.success(data.DeleteEmail.message);
                // Assume backend returns deleted email _id in data.DeleteEmail.deletedId
                return email;
            } else {
                message.error(data.DeleteEmail.message || 'Failed to delete email');
                return rejectWithValue(data.DeleteEmail.message);
            }
        } catch (error) {
            message.error('An error occurred.');
            return rejectWithValue(error.message);
        } finally {
            dispatch(setLoader(false));
        }
    }
);

const emailsSlice = createSlice({
    name: 'emails',
    initialState: {
        emails: [],
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEmails.fulfilled, (state, action) => {
                state.emails = action.payload || [];
            })
            .addCase(addEmail.fulfilled, (state, action) => {
                if (action.payload) {
                    state.emails.push(action.payload); // Add new email to state
                }
            })
            .addCase(editEmail.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.emails.findIndex(email => email._id === action.payload._id);
                    if (index !== -1) {
                        state.emails[index] = action.payload; // Update email in state
                    }
                }
            })
            .addCase(deleteEmail.fulfilled, (state, action) => {
                if (action.payload) {
                    state.emails = state.emails.filter(email => email.email !== action.payload);
                }
            });

    },
});

export const updateEmpEmailSettingsThunk = createAsyncThunk(
    'emailSettings/update',
    async ({ employeeId, emailSettings }, thunkAPI) => {
        try {
            const response = await client.mutate({
                mutation: UPDATE_EMP_EMAIL_SETTINGS,
                variables: {
                    employeeId,
                    emailSettings,
                },
            });
            const result = response?.data?.UpdateEmpEmailSettings;

            if (result?.success) {
                return result; // Fulfilled
            } else {
                return thunkAPI.rejectWithValue(result?.message || 'Failed to update email settings');
            }
        } catch (error) {
            console.error('Thunk error:', error);
            return thunkAPI.rejectWithValue('An unexpected error occurred');
        }
    }
);

export default emailsSlice.reducer;
